import { useQuery } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  notFound,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';

import { LabelMapSchema } from '@/api';
import { Button } from '@/components/Button/Button';
import { Explorer } from '@/components/Explorer';
import { ModalTrigger } from '@/components/Modal';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { TextButton } from '@/components/TextButton/TextButton';
import { RegularChevronRight } from '@/icons/components';
import { Label } from '@/modals/Label';
import { LabelMap } from '@/modals/LabelMap';
import { useIsExactMatch } from '@/utils/hooks/useIsExactMatch';

import { ExplorerButtonLabel } from './-components/ExplorerButtonLabel';
import { ExplorerButtonLabelMap } from './-components/ExplorerButtonLabelMap';
import { SidebarLayout } from './-components/SidebarLayout';
import { useLabel, useLabelMap, useLabelMapId } from '@/api/queries/label';

const searchSchema = z.object({
  query: z.string().optional(),
  mapPage: z.number().optional(),
  labelPage: z.number().optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

/**
 * Query options
 */
const labelMapQueryOptions = (mapId: string, search: SearchParams) => {
  return useLabelMap.getOptions({
    size: 20,
    page: search.mapPage ?? 1,
    filter: [
      {
        field: 'Map',
        value: mapId,
        op: 'eq',
      },
    ],
    sort: [
      {
        field: 'Naam',
        direction: 'asc',
      },
    ],
  });
};

const labelQueryOptions = (mapId: string, search: SearchParams) => {
  return useLabel.getOptions({
    size: 20,
    page: search.labelPage ?? 1,
    filter: [
      {
        model: 'Label',
        field: 'Map',
        value: mapId,
        op: 'eq',
      },
    ],
    sort: [
      {
        model: 'Label',
        field: 'Label',
        direction: 'asc',
      },
    ],
  });
};

const labelMapIdQueryOptions = (mapId: string) =>
  useLabelMapId.getOptions(mapId);

/**
 * Route
 */
export const Route = createFileRoute('/beheer/labels/$mapId')({
  validateSearch: searchSchema.parse,
  beforeLoad: ({ context, params }) => ({
    getTitle: () => {
      const { queryKey } = labelMapIdQueryOptions(params.mapId);

      return context.queryClient.getQueryData<LabelMapSchema>(queryKey)?.Naam;
    },
  }),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context: { queryClient }, params: { mapId }, deps }) => {
    const [labelMap, mappen, labels] = await Promise.all([
      queryClient.ensureQueryData(labelMapIdQueryOptions(mapId)),
      queryClient.ensureQueryData(labelMapQueryOptions(mapId, deps.search)),
      queryClient.ensureQueryData(labelQueryOptions(mapId, deps.search)),
    ]);

    if (!labelMap) throw notFound();

    if (deps.search.mapPage && (mappen.num_pages ?? 1) < deps.search.mapPage) {
      if (mappen.num_pages) {
        throw redirect({
          to: '/beheer/labels/$mapId',
          search: { p: 1, mapPage: mappen.num_pages, labelPage: labels.page },
          params: { mapId },
        });
      }
    }

    if (
      deps.search.labelPage &&
      (labels.num_pages ?? 1) < deps.search.labelPage
    ) {
      if (labels.num_pages) {
        throw redirect({
          to: '/beheer/labels/$mapId',
          search: { p: 1, mapPage: mappen.page, labelPage: labels.num_pages },
          params: { mapId },
        });
      }
    }
  },
  component: LabelsComponent,
});

/**
 * Route component
 */
function LabelsComponent() {
  const { mapId } = Route.useParams();

  const search = Route.useSearch({
    select: ({ mapPage, labelPage }) => ({
      mapPage: mapPage ?? 1,
      labelPage: labelPage ?? 1,
    }),
  });

  const map = useQuery(labelMapIdQueryOptions(mapId));
  const mappen = useQuery(labelMapQueryOptions(mapId, search));
  const labels = useQuery(labelQueryOptions(mapId, search));

  /**
   * NOTE: Temporary solution for breadcrumbs
   *
   * When the route is not exactly this route, render the outlet
   */
  const isExactMatch = useIsExactMatch(Route.fullPath);

  if (!isExactMatch) {
    return <Outlet />;
  }

  return (
    <SidebarLayout>
      <Explorer
        backButton={
          <TextButton
            className="mb-0.5 flex items-center"
            variant="gray"
            size="small"
            asChild
          >
            <Link from={Route.fullPath} to="/beheer/labels" search={{ p: 1 }}>
              <RegularChevronRight
                size={14}
                className="mr-1 inline-block rotate-180"
              />
              <span>Terug</span>
            </Link>
          </TextButton>
        }
        title={map.data?.Naam}
      >
        <Explorer.ItemHeading
          title="Mappen"
          button={
            <ModalTrigger
              modalComponent={LabelMap}
              onOpen={() => ({ createMap: true, newMapParentId: mapId })}
              asChild
            >
              <Button variant="secondary">Nieuwe map</Button>
            </ModalTrigger>
          }
        />

        {!mappen.data?.objects?.length ? (
          <Explorer.Placeholder>
            Deze map heeft geen mappen
          </Explorer.Placeholder>
        ) : (
          <>
            <ul>
              {mappen.data?.objects?.map((map) => (
                <li key={`label-map-link-${map.ID}`}>
                  <ExplorerButtonLabelMap asChild map={map}>
                    <Link
                      to="/beheer/labels/$mapId/$nestedMapId"
                      search={{ p: 1 }}
                      params={{ mapId: mapId, nestedMapId: map.ID! }}
                    >
                      {map.Naam}
                    </Link>
                  </ExplorerButtonLabelMap>
                </li>
              ))}
            </ul>

            {(mappen.data?.num_pages ?? 0) > 1 ? (
              <div className="mt-5 flex w-full justify-center">
                <PaginationLinks
                  aria-label="Label mappen pagina's"
                  renderLink={({ children, page }) => (
                    <Link
                      to="/beheer/labels/$mapId"
                      params={{ mapId }}
                      search={{
                        p: 1,
                        labelPage: search.labelPage,
                        mapPage: page,
                      }}
                    >
                      {children}
                    </Link>
                  )}
                  currentPage={search.mapPage ?? 1}
                  totalPages={mappen.data?.num_pages ?? 0}
                />
              </div>
            ) : null}
          </>
        )}

        <Explorer.ItemHeading
          title="Labels"
          button={
            <ModalTrigger
              modalComponent={Label}
              onOpen={() => ({ createLabel: { mapId } })}
              asChild
            >
              <Button variant="secondary">Nieuw label</Button>
            </ModalTrigger>
          }
        />

        {!labels.data?.objects?.length ? (
          <Explorer.Placeholder>
            Deze map heeft geen labels
          </Explorer.Placeholder>
        ) : (
          <>
            <ul>
              {labels.data?.objects?.map((label) => (
                <li key={`label-button-${label.ID}`}>
                  <ExplorerButtonLabel label={label} />
                </li>
              ))}
            </ul>

            {(labels.data?.num_pages ?? 0) > 1 ? (
              <div className="mt-5 flex w-full justify-center">
                <PaginationLinks
                  aria-label="Label pagina's"
                  renderLink={({ children, page }) => (
                    <Link
                      to="/beheer/labels/$mapId"
                      params={{ mapId }}
                      search={{
                        p: 1,
                        mapPage: search.mapPage,
                        labelPage: page,
                      }}
                    >
                      {children}
                    </Link>
                  )}
                  currentPage={search.labelPage}
                  totalPages={labels.data?.num_pages ?? 0}
                />
              </div>
            ) : null}
          </>
        )}
      </Explorer>
    </SidebarLayout>
  );
}
